import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import type { LeadClassification, LeadClassificationInput, ManualLeadSource } from '@valutten/shared-schemas';
import { functions } from '../lib/firebase';

export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'disqualified';
export type LeadStatusFilter = LeadStatus | 'all';

export interface LeadRecord {
  leadId: string;
  companyId: string;
  source: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  status: string;
  assignedBrokerId: string | null;
  assignedBrokerName: string | null;
  officeId: string | null;
  capturedAt: string | null;
  updatedAt: string | null;
  firstContactedAt: string | null;
  firstContactDueAt: string | null;
  passOffDueAt: string | null;
  queuedUntil: string | null;
  serviceCategory: string | null;
  dealType: string | null;
  // The four classification answers; null for leads captured before them.
  classification?: LeadClassification | null;
  convertedAt: string | null;
  mycrmLeadId: string | null;
  linkedLoanId: string | null;
  attribution: {
    utmSource: string | null;
    utmCampaign: string | null;
    utmMedium: string | null;
    utmContent: string | null;
    utmTerm: string | null;
    referrerUrl: string | null;
  };
  consent: {
    marketingConsent: boolean;
    consentAt: string | null;
  };
}

export interface InboxStats {
  todayCount: number;
  weekCount: number;
  avgResponseMinutes: number | null;
  assignmentCoveragePercent: number;
  totalCount: number;
  truncated: boolean;
}

export interface LeadCountsBySource {
  days: number;
  total: number;
  counts: Array<{ source: string; count: number }>;
}

export interface WebhookTokenConfig {
  configured: boolean;
  token: string | null;
  disabled: boolean;
  generation: number;
  rotatedAt: string | null;
  graceExpiresAt: string | null;
  monitoringEnabled: boolean;
  maxSilenceHours: number;
}

/** Exact per-status totals, counted server-side within the caller's scope. */
export type LeadStatusCounts = Record<LeadStatus, number>;

const EMPTY_STATUS_COUNTS: LeadStatusCounts = {
  new: 0,
  contacted: 0,
  qualified: 0,
  converted: 0,
  disqualified: 0,
};

interface UseLeadsState {
  leads: LeadRecord[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /**
   * Per-status totals for the filter chips. Counted on the server over the
   * caller's scoped query, NOT derived from `leads`. `leads` is one page of
   * ONE status, so deriving from it made every other chip read 0.
   */
  statusCounts: LeadStatusCounts;
  statusCountsLoading: boolean;
  /** True when another page exists past the ones already loaded. */
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  setStatusFilter: (s: LeadStatusFilter) => void;
  setAssignedToMe: (v: boolean) => void;
  statusFilter: LeadStatusFilter;
  assignedToMe: boolean;
  /**
   * eDM lead-tag filter (edm_content only): a registry tag name sent as
   * listLeads `tagNorm`. null = no filter, and the key is then omitted so the
   * request is identical to the pre-tag one.
   */
  tagNorm: string | null;
  setTagNorm: (t: string | null) => void;
  updateLead: (args: {
    leadId: string;
    status?: LeadStatus;
    assignedBrokerId?: string | null;
    assignedBrokerName?: string | null;
    notes?: string;
    // null clears the answers; ids are checked server-side.
    classification?: LeadClassificationInput | null;
  }) => Promise<void>;
  declineLead: (args: { leadId: string; reason?: string }) => Promise<{
    outcome: 'reassigned' | 'pool_exhausted';
    newAssigneeUid: string | null;
  }>;
  generateWebhookToken: () => Promise<string>;
  getWebhookTokenConfig: () => Promise<WebhookTokenConfig>;
  rotateWebhookToken: () => Promise<{
    token: string;
    generation: number;
    graceExpiresAt: string;
  }>;
  enableWebhookMonitoring: () => Promise<{
    monitoringEnabled: true;
    maxSilenceHours: number;
  }>;
  setWebhookDisabled: (disabled: boolean) => Promise<void>;
  // Manual lead entry: operator hand-keys a lead captured off-platform
  // (inbound call, or another CRM). Routes/triages like any captured lead.
  createManualLead: (args: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    serviceCategory: string;
    source: ManualLeadSource;
    notes?: string;
    classification?: LeadClassificationInput;
  }) => Promise<{ leadId: string; deduped: boolean }>;
  getInboxStats: () => Promise<InboxStats>;
  getLeadCountsBySource: (days?: number) => Promise<LeadCountsBySource>;
  // Manual lead→loan attribution. Pass loanId=null to unlink.
  linkLeadToLoan: (args: {
    leadId: string;
    loanId: string | null;
  }) => Promise<{ linkedLoanId: string | null }>;
}

export function useLeads(companyId: string | undefined): UseLeadsState {
  const [leads, setLeads] = useState<LeadRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<LeadStatusFilter>('new');
  const [assignedToMe, setAssignedToMe] = useState<boolean>(false);
  const [tagNorm, setTagNorm] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] =
    useState<LeadStatusCounts>(EMPTY_STATUS_COUNTS);
  const [statusCountsLoading, setStatusCountsLoading] = useState<boolean>(
    Boolean(companyId)
  );
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  // Bumped per first-page request: a reply (first page or Load more) for an
  // older filter is dropped. The tagNorm filter takes a slower server path,
  // so out-of-order replies became likely with it.
  const listSeq = useRef(0);
  // listSeq value of the first page that produced nextCursor; Load more
  // during a reload would otherwise pair the old cursor with the new filter.
  const cursorSeq = useRef(0);

  const PAGE_SIZE = 100;

  // First page. Changing filter/scope resets pagination.
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const fn = httpsCallable<
      {
        action: 'listLeads';
        companyId: string;
        status: LeadStatusFilter;
        limit: number;
        assignedToMe: boolean;
        tagNorm?: string;
      },
      { leads: LeadRecord[]; hasMore?: boolean; nextCursor?: string | null }
    >(functions, 'leadAction');

    const mine = ++listSeq.current;
    setLoading(true);
    setError(null);
    fn({
      action: 'listLeads',
      companyId,
      status: statusFilter,
      limit: PAGE_SIZE,
      assignedToMe,
      ...(tagNorm ? { tagNorm } : {}),
    })
      .then((r) => {
        if (mine !== listSeq.current) return;
        cursorSeq.current = mine;
        setLeads(r.data.leads);
        setHasMore(Boolean(r.data.hasMore));
        setNextCursor(r.data.nextCursor ?? null);
      })
      .catch((err) => {
        if (mine !== listSeq.current) return;
        console.error('[useLeads] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (mine === listSeq.current) setLoading(false);
      });
  }, [companyId, statusFilter, assignedToMe, tagNorm, refreshKey]);

  const loadMore = useCallback(() => {
    if (!companyId || !nextCursor || loadingMore) return;
    if (cursorSeq.current !== listSeq.current) return;
    const fn = httpsCallable<
      {
        action: 'listLeads';
        companyId: string;
        status: LeadStatusFilter;
        limit: number;
        assignedToMe: boolean;
        tagNorm?: string;
        cursor: string;
      },
      { leads: LeadRecord[]; hasMore?: boolean; nextCursor?: string | null }
    >(functions, 'leadAction');

    const mine = listSeq.current;
    setLoadingMore(true);
    fn({
      action: 'listLeads',
      companyId,
      status: statusFilter,
      limit: PAGE_SIZE,
      assignedToMe,
      ...(tagNorm ? { tagNorm } : {}),
      cursor: nextCursor,
    })
      .then((r) => {
        if (mine !== listSeq.current) return;
        // De-duplicate on leadId: a lead captured between pages shifts the
        // window and can otherwise repeat a row.
        setLeads((prev) => {
          const seen = new Set(prev.map((l) => l.leadId));
          return [...prev, ...r.data.leads.filter((l) => !seen.has(l.leadId))];
        });
        setHasMore(Boolean(r.data.hasMore));
        setNextCursor(r.data.nextCursor ?? null);
      })
      .catch((err) => {
        if (mine !== listSeq.current) return;
        console.error('[useLeads] loadMore failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoadingMore(false));
  }, [companyId, statusFilter, assignedToMe, tagNorm, nextCursor, loadingMore]);

  // Per-status chip totals. Independent of the status filter (that is the
  // whole point), so it re-runs only on scope change or an explicit refresh.
  useEffect(() => {
    if (!companyId) {
      setStatusCountsLoading(false);
      return;
    }
    let cancelled = false;
    const fn = httpsCallable<
      { action: 'getLeadStatusCounts'; companyId: string; assignedToMe: boolean },
      { counts: LeadStatusCounts; total: number }
    >(functions, 'leadAction');

    setStatusCountsLoading(true);
    fn({ action: 'getLeadStatusCounts', companyId, assignedToMe })
      .then((r) => {
        if (!cancelled) setStatusCounts(r.data.counts ?? EMPTY_STATUS_COUNTS);
      })
      .catch((err) => {
        // Never silently swallow: the chips fall back to zeroes, and the
        // failure is visible in the console rather than reading as "no leads".
        console.error('[useLeads] status counts failed', err);
        if (!cancelled) setStatusCounts(EMPTY_STATUS_COUNTS);
      })
      .finally(() => {
        if (!cancelled) setStatusCountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, assignedToMe, refreshKey]);

  const updateLead = useCallback(
    async (args: {
      leadId: string;
      status?: LeadStatus;
      assignedBrokerId?: string | null;
      assignedBrokerName?: string | null;
      notes?: string;
      classification?: LeadClassificationInput | null;
    }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        { action: 'updateLead'; companyId: string } & typeof args,
        { success: boolean }
      >(functions, 'leadAction');
      await fn({ action: 'updateLead', companyId, ...args });
      setRefreshKey((k) => k + 1);
    },
    [companyId]
  );

  const declineLead = useCallback(
    async (args: { leadId: string; reason?: string }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'declineLead';
          companyId: string;
          leadId: string;
          reason?: string;
        },
        {
          success: boolean;
          outcome: 'reassigned' | 'pool_exhausted';
          newAssigneeUid: string | null;
        }
      >(functions, 'leadAction');
      const result = await fn({ action: 'declineLead', companyId, ...args });
      setRefreshKey((k) => k + 1);
      return {
        outcome: result.data.outcome,
        newAssigneeUid: result.data.newAssigneeUid,
      };
    },
    [companyId]
  );

  const generateWebhookToken = useCallback(async () => {
    if (!companyId) throw new Error('companyId required');
    const fn = httpsCallable<
      { action: 'generateWebhookToken'; companyId: string },
      { success: boolean; token: string }
    >(functions, 'leadAction');
    const result = await fn({ action: 'generateWebhookToken', companyId });
    return result.data.token;
  }, [companyId]);

  const getWebhookTokenConfig = useCallback(async () => {
    if (!companyId) throw new Error('companyId required');
    const fn = httpsCallable<
      { action: 'getWebhookTokenConfig'; companyId: string },
      { success: boolean } & WebhookTokenConfig
    >(functions, 'leadAction');
    const result = await fn({ action: 'getWebhookTokenConfig', companyId });
    return result.data;
  }, [companyId]);

  const rotateWebhookToken = useCallback(async () => {
    if (!companyId) throw new Error('companyId required');
    const fn = httpsCallable<
      {
        action: 'rotateWebhookToken';
        companyId: string;
        confirmRotation: true;
      },
      {
        success: boolean;
        token: string;
        generation: number;
        graceExpiresAt: string;
      }
    >(functions, 'leadAction');
    const result = await fn({
      action: 'rotateWebhookToken',
      companyId,
      confirmRotation: true,
    });
    return result.data;
  }, [companyId]);

  const enableWebhookMonitoring = useCallback(async () => {
    if (!companyId) throw new Error('companyId required');
    const fn = httpsCallable<
      { action: 'enableWebhookMonitoring'; companyId: string },
      {
        success: boolean;
        monitoringEnabled: true;
        maxSilenceHours: number;
      }
    >(functions, 'leadAction');
    const result = await fn({
      action: 'enableWebhookMonitoring',
      companyId,
    });
    return {
      monitoringEnabled: result.data.monitoringEnabled,
      maxSilenceHours: result.data.maxSilenceHours,
    };
  }, [companyId]);

  const setWebhookDisabled = useCallback(
    async (disabled: boolean) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        { action: 'disableWebhook'; companyId: string; disabled: boolean },
        { success: boolean }
      >(functions, 'leadAction');
      await fn({ action: 'disableWebhook', companyId, disabled });
    },
    [companyId]
  );

  const createManualLead = useCallback(
    async (args: {
      firstName: string;
      lastName: string;
      email?: string;
      phone?: string;
      serviceCategory: string;
      source: ManualLeadSource;
      notes?: string;
      classification?: LeadClassificationInput;
    }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        { companyId: string } & typeof args,
        { success: boolean; leadId: string; deduped: boolean }
      >(functions, 'createManualLead');
      const result = await fn({ companyId, ...args });
      setRefreshKey((k) => k + 1);
      return { leadId: result.data.leadId, deduped: result.data.deduped };
    },
    [companyId]
  );

  const getInboxStats = useCallback(async () => {
    if (!companyId) throw new Error('companyId required');
    const fn = httpsCallable<
      { action: 'getInboxStats'; companyId: string },
      InboxStats
    >(functions, 'leadAction');
    const result = await fn({ action: 'getInboxStats', companyId });
    return result.data;
  }, [companyId]);

  const linkLeadToLoan = useCallback(
    async (args: { leadId: string; loanId: string | null }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'linkLeadToLoan';
          companyId: string;
          leadId: string;
          loanId: string | null;
        },
        { success: boolean; linkedLoanId: string | null }
      >(functions, 'leadAction');
      const result = await fn({ action: 'linkLeadToLoan', companyId, ...args });
      setRefreshKey((k) => k + 1);
      return { linkedLoanId: result.data.linkedLoanId };
    },
    [companyId]
  );

  const getLeadCountsBySource = useCallback(
    async (days = 30) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        { action: 'getLeadCountsBySource'; companyId: string; days: number },
        LeadCountsBySource
      >(functions, 'leadAction');
      const result = await fn({
        action: 'getLeadCountsBySource',
        companyId,
        days,
      });
      return result.data;
    },
    [companyId]
  );

  return {
    leads,
    loading,
    error,
    refresh,
    statusCounts,
    statusCountsLoading,
    hasMore,
    loadingMore,
    loadMore,
    statusFilter,
    setStatusFilter,
    assignedToMe,
    setAssignedToMe,
    tagNorm,
    setTagNorm,
    updateLead,
    declineLead,
    generateWebhookToken,
    getWebhookTokenConfig,
    rotateWebhookToken,
    enableWebhookMonitoring,
    setWebhookDisabled,
    createManualLead,
    getInboxStats,
    getLeadCountsBySource,
    linkLeadToLoan,
  };
}
